import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Star, Clock, Users, MapPin, ArrowLeft, Calendar, Facebook, Instagram, Tag } from 'lucide-react';
import { GET_PACKAGE_DETAILS } from '../graphql/queries/package';
import Gallery from '../components/Gallery';
import ReviewList from '../components/ReviewList';
import Itinerary from '../components/Itinerary';
import PackageFormulas from '../components/PackageFormulas';
import PackageCard from '../components/PackageCard';
import DownloadPackageButton from '../components/pdf/DownloadPackageButton';
import AgencyInfoCard from '../components/agency/AgencyInfoCard';
import AirlineBadge from '../components/AirlineBadge';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TikTokIcon } from '../components/icons/TikTokIcon';
import SocialLinks from '../components/SocialLinks';
import { formatPrice, getDisplayPrice } from '../utils/price';
import { getImageUrl } from '../utils/imageUrl';
import { getCategoryLabel } from '../utils/categoryLabel';
import type { 
  Formula, 
  SimilarPackage, 
  PackageDetails,
  Theme
} from '../types/pdf';
import { DEFAULT_THEME } from '../constants/theme';

const parseTheme = (theme: any): Partial<Theme> => {
  try {
    if (!theme) return {};
    
    // Si c'est un objet, nettoyer et valider les valeurs
    if (typeof theme === 'object' && theme !== null) {
      
      // Supprimer __typename et filtrer les valeurs nulles
      const { __typename, ...themeValues } = theme;
      const cleanTheme = Object.entries(themeValues).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key as keyof Theme] = value;
        }
        return acc;
      }, {} as Partial<Theme>);

      return cleanTheme;
    }
    
    // Si c'est une chaîne JSON, la parser
    if (typeof theme === 'string') {
      try {
        const parsedTheme = JSON.parse(theme);
        return parseTheme(parsedTheme); // Réutiliser la logique de nettoyage
      } catch (error) {
        console.error('Erreur de parsing JSON:', error);
        return {};
      }
    }
    
    console.warn('Format de thème non reconnu:', theme);
    return {};
  } catch (error) {
    console.error('Erreur lors du traitement du thème:', error, 'thème reçu:', theme);
    return {};
  }
};

export default function PackageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'itinerary' | 'gallery' | 'reviews'>('overview');
  const [selectedFormula, setSelectedFormula] = useState<Formula | null>(null);
  const [showReserverModal, setShowReserverModal] = useState(false);

  const { loading, error, data } = useQuery<{ package: PackageDetails }>(
    GET_PACKAGE_DETAILS,
    {
      variables: { 
        id: id ? parseInt(id) : 0 
      },
      skip: !id || isNaN(parseInt(id)),
      onError: (error) => {
        console.error('❌ Erreur Apollo détaillée:', {
          message: error.message,
          graphQLErrors: error.graphQLErrors?.map(err => ({
            message: err.message,
            path: err.path,
            extensions: err.extensions
          })),
          networkError: error.networkError,
          extraInfo: error.extraInfo
        });
      }
    }
  );

  const formatDate = (dateString: string | number | Date | null | undefined) => {
    try {
      if (!dateString) {
        return 'Date à confirmer';
      }
      
      let date: Date;
      
      // Si c'est un timestamp sous forme de chaîne ou de nombre
      if (typeof dateString === 'string' && !isNaN(Number(dateString))) {
        date = new Date(Number(dateString));
      }
      // Si c'est un timestamp (nombre)
      else if (typeof dateString === 'number') {
        date = new Date(dateString);
      }
      // Si c'est déjà un objet Date
      else if (dateString instanceof Date) {
        date = dateString;
      }
      // Si c'est une chaîne de caractères (format date)
      else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.error('Date invalide:', dateString);
        return 'Date à confirmer';
      }
      
      return format(date, 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error, 'pour la date:', dateString);
      return 'Date à confirmer';
    }
  };

  // Si l'ID n'est pas valide, rediriger vers la page d'accueil
  if (!id || isNaN(parseInt(id))) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">ID de package invalide</h2>
        <p className="text-gray-600 mb-8">L'identifiant du package n'est pas valide.</p>
        <button
          onClick={() => navigate('/home')}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" color="blue-600" />
      </div>
    );
  }

  if (error || !data?.package) {
    console.error('❌ Erreur ou pas de données:', {
      error: error ? {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      } : null,
      data
    });
    return (
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold mb-4">Package non trouvé</h2>
        <p className="text-gray-600 mb-8">
          {error ? `Erreur: ${error.message}` : 'Le package que vous recherchez n\'existe pas ou a été supprimé.'}
        </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Retour à l'accueil
          </button>
        </div>
    );
  }

  const pkg = data.package;
  
  // Traiter le thème quelle que soit sa forme
  const agencyTheme = parseTheme(pkg.agency?.theme);
  
  // Fusionner avec le thème par défaut en s'assurant d'avoir des valeurs valides
  const theme = {
    ...DEFAULT_THEME,
    ...agencyTheme
  };




  // Obtenir la prochaine date de départ
  const getNextDeparture = () => {
    if (!pkg.departures || pkg.departures.length === 0) return null;

    const now = new Date();
    const futureDepartures = pkg.departures
      .map(dep => ({
        ...dep,
        departureDate: new Date(dep.departureDate),
        returnDate: new Date(dep.returnDate)
      }))
      .filter(dep => dep.departureDate >= now)
      .sort((a, b) => a.departureDate.getTime() - b.departureDate.getTime());

    return futureDepartures[0] || pkg.departures[0];
  };

  const nextDeparture = getNextDeparture();

  // Mettre à jour le calcul de la durée
  const getDuration = () => {
    if (!nextDeparture) return { days: 0, nights: 0 };

    try {
      // Convertir les timestamps en nombres
      const departureTimestamp = typeof nextDeparture.departureDate === 'string' 
        ? parseInt(nextDeparture.departureDate) 
        : nextDeparture.departureDate;
        
      const returnTimestamp = typeof nextDeparture.returnDate === 'string' 
        ? parseInt(nextDeparture.returnDate) 
        : nextDeparture.returnDate;

      // Créer les objets Date à partir des timestamps
      const departureDate = new Date(departureTimestamp);
      const returnDate = new Date(returnTimestamp);
      
      if (isNaN(departureDate.getTime()) || isNaN(returnDate.getTime())) {
        console.error('Dates invalides après conversion:', {
          departure: {
            original: nextDeparture.departureDate,
            timestamp: departureTimestamp,
            date: departureDate
          },
          return: {
            original: nextDeparture.returnDate,
            timestamp: returnTimestamp,
            date: returnDate
          }
        });
        return { days: 0, nights: 0 };
      }
      
      const days = differenceInDays(returnDate, departureDate);
      return {
        days: Math.max(days, 0),
        nights: Math.max(days - 1, 0)
      };
    } catch (error) {
      console.error('Erreur de calcul de durée:', error, 'pour les dates:', {
        departure: {
          value: nextDeparture.departureDate,
          type: typeof nextDeparture.departureDate
        },
        return: {
          value: nextDeparture.returnDate,
          type: typeof nextDeparture.returnDate
        }
      });
      return { days: 0, nights: 0 };
    }
  };

  const { days, nights } = getDuration();

  return (
    <div 
      className="min-h-screen flex flex-col" 
      style={{ 
        backgroundColor: theme.backgroundColor || DEFAULT_THEME.backgroundColor 
      }}
    >
      {/* Bouton retour */}
      <div className="fixed top-4 left-4 z-50">
        <button
          type="button"
          onClick={() => navigate('/home')}
          style={{ 
            backgroundColor: theme.primaryColor || DEFAULT_THEME.primaryColor,
            color: theme.secondaryColor || DEFAULT_THEME.secondaryColor,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
          className="flex items-center px-4 py-2 rounded-lg hover:opacity-90 transition-opacity duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span>Retour à l&apos;accueil</span>
        </button>
      </div>
      
      {/* Hero Section */}
      <div className="relative h-96">
        <img src={getImageUrl(pkg.image)} alt={pkg.title} className="w-full h-full object-cover" />
        {/* Double gradient pour assurer la lisibilité */}
        <div 
          className="absolute inset-0" 
          style={{ 
            background: `
              linear-gradient(to bottom, 
                rgba(0, 0, 0, 0.4), 
                rgba(0, 0, 0, 0.6)
              ),
              linear-gradient(to bottom, 
                ${theme.primaryColor}20, 
                ${theme.primaryColor}40
              )
            `
          }}
        />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 text-white">
            <h1 className="text-4xl font-bold mb-3 text-shadow">{pkg.title}</h1>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-white/20 text-white border border-white/30"
              >
                <Tag className="w-4 h-4 shrink-0" aria-hidden />
                {getCategoryLabel(pkg.category)}
              </span>
              {pkg.airline ? (
                <AirlineBadge airline={pkg.airline} variant="labeled" tone="hero" />
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              {/* Dates */}
              <div className="space-y-4">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  <div>
                    <span className="font-medium">Départ :</span>{' '}
                    {nextDeparture ? (
                      formatDate(nextDeparture.departureDate)
                    ) : (
                      'Dates à venir'
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  <div>
                    <span className="font-medium">Retour :</span>{' '}
                    {nextDeparture ? (
                      formatDate(nextDeparture.returnDate)
                    ) : (
                      'Dates à venir'
                    )}
                  </div>
                </div>
              </div>
              
              {/* Durée */}
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <span>{days} jours / {nights} nuits</span>
              </div>

              {/* Lieu de départ */}
              <div className="flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Départ de {pkg.departure || 'À confirmer'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {(['overview', 'itinerary', 'gallery', 'reviews'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{ 
                  color: activeTab === tab ? theme.primaryColor : theme.textColor,
                  borderBottom: activeTab === tab ? `2px solid ${theme.primaryColor}` : 'none'
                }}
                className="py-4 px-1 font-medium hover:opacity-80 transition-colors"
              >
                {{ overview: 'Résumé', itinerary: 'Itinéraire', gallery: 'Galerie', reviews: 'Avis' }[tab]}
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div>
                  <h2 
                    className="text-2xl font-bold mb-4"
                    style={{ color:'#374151' }}
                  >
                    Description
                  </h2>
                  <p style={{ color: '#374151' }}>{pkg.description}</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-4">Points forts</h3>
                  <ul className="grid grid-cols-2 gap-4">
                    {pkg.highlights.map((highlight: string, index: number) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <span 
                          className="w-2 h-2 rounded-full mr-2" 
                          style={{ backgroundColor: theme.primaryColor }}
                        />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold mb-4">Inclus</h3>
                    <ul className="space-y-2">
                      {pkg.included.map((item: string, index: number) => (
                        <li key={index} className="flex items-center text-gray-600">
                          <span 
                            className="w-2 h-2 rounded-full mr-2" 
                            style={{ backgroundColor: theme.primaryColor }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-4">Non inclus</h3>
                    <ul className="space-y-2">
                      {pkg.excluded.map((item: string, index: number) => (
                        <li key={index} className="flex items-center text-gray-600">
                          <span 
                            className="w-2 h-2 rounded-full mr-2" 
                            style={{ backgroundColor: theme.secondaryColor }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 
                    className="text-xl font-bold mb-4"
                    style={{ color: '#374151' }}
                  >
                    Formules disponibles
                  </h3>
                  <PackageFormulas
                    formulas={pkg.formulas}
                    selectedFormulaId={selectedFormula?.id}
                    onSelectFormula={setSelectedFormula}
                    theme={theme}
                  />
                </div>
              </div>
            )}

            {activeTab === 'itinerary' && <Itinerary itinerary={pkg.itinerary} />}
            {activeTab === 'gallery' && <div>Galerie non disponible</div>}
            {activeTab === 'reviews' && <div>Avis prochainement disponible</div>}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6 sticky top-24">
              {/* Informations de l'agence */}
              <AgencyInfoCard agency={pkg.agency} />

              {/* Actions */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                {selectedFormula ? (
                  <>
                    <div className="text-center mb-6">
                      <div 
                        className="text-3xl font-bold mb-2"
                        style={{ color: theme.primaryColor }}
                      >
                        {selectedFormula.discountPercent && selectedFormula.discountPercent > 0 && (
                          <span 
                            className="line-through mr-2 text-xl"
                            style={{ color: '#181817' + '80' }}
                          >
                            {formatPrice(selectedFormula)}
                          </span>
                        )}
                        {formatPrice({
                          ...selectedFormula,
                          perPersonPrice: selectedFormula.perPersonPrice 
                            ? selectedFormula.perPersonPrice * (1 - (selectedFormula.discountPercent || 0) / 100)
                            : null,
                          roomPrices: Object.entries(selectedFormula.roomPrices).reduce((acc, [key, value]) => ({
                            ...acc,
                            [key]: value * (1 - (selectedFormula.discountPercent || 0) / 100)
                          }), {} as typeof selectedFormula.roomPrices)
                        })}
                      </div>
                      <div className="text-sm text-gray-500">
                        {selectedFormula.priceType === 'PER_PERSON' ? 'par personne' : 'par personne'}
                      </div>
                    </div>

                    <button 
                      type="button"
                      onClick={() => setShowReserverModal(true)}
                      style={{ 
                        backgroundColor: theme.secondaryColor,
                        color: theme.primaryColor,
                      }}
                      className="w-full py-3 rounded-lg hover:opacity-90 transition mb-4"
                    >
                      Réserver cette formule
                    </button>
                  </>
                ) : (
                  <div className="text-center text-gray-500 mb-4">
                    Sélectionnez une formule pour réserver
                  </div>
                )}

                <DownloadPackageButton
                  package={pkg}
                  agency={pkg.agency}
                  className="mt-4"
                  theme={theme}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Packages similaires */}
        {pkg.similarPackages && pkg.similarPackages.length > 0 && (
          <div className="mt-12">
            <h2 
              className="text-2xl font-bold mb-6" 
              style={{ 
                color: theme.textColor || DEFAULT_THEME.textColor 
              }}
            >
              Packages similaires
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {pkg.similarPackages.map(similarPackage => (
                <PackageCard 
                  key={similarPackage.id} 
                  package={{
                    ...similarPackage,
                    airline: similarPackage.airline ?? undefined,
                    agency: similarPackage.agency
                      ? {
                          id: similarPackage.agency.id,
                          name: similarPackage.agency.name,
                          logo: similarPackage.agency.logo ?? '',
                          rating: similarPackage.agency.rating ?? undefined,
                          verified: similarPackage.agency.verified,
                          secondaryColor:
                            similarPackage.agency.theme?.secondaryColor ?? null,
                        }
                      : undefined,
                  }}
                  primaryColor={
                    similarPackage.agency?.theme?.primaryColor ||
                    theme.primaryColor ||
                    DEFAULT_THEME.primaryColor
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Social Links */}
      <SocialLinks
        facebook={pkg.agency.facebook}
        instagram={pkg.agency.instagram}
        tiktok={pkg.agency.tiktok}
        primaryColor={theme.primaryColor}
      />

      <Modal
        isOpen={showReserverModal}
        onClose={() => setShowReserverModal(false)}
        title="Prochainement disponible"
      >
        <p className="text-gray-600">La réservation en ligne sera bientôt disponible.</p>
      </Modal>
    </div>
  );
}